import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAffAuth } from "../store";
import { formatApiError } from "../api";
import { useT, useAffLangInit } from "../i18n";
import LangPicker from "../LangPicker";

export default function AffLogin() {
  const { login } = useAffAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  useAffLangInit();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      const dest = location.state?.from?.pathname || "/affiliate";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="dark fixed inset-0 flex items-center justify-center bg-black overflow-hidden"
      data-testid="aff-login-screen"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-emerald-500/[0.06] blur-3xl" />
        <div className="absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full bg-amber-500/[0.05] blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(hsl(0 0% 30%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 30%) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>
      <form
        onSubmit={submit}
        className="relative z-10 w-full max-w-sm bg-black/60 border border-white/10 backdrop-blur-xl p-8 space-y-6"
        data-testid="aff-login-form"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display text-3xl font-medium text-white tracking-tight">
              Clipra
            </div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-white/40 mt-1">
              {t("login.subtitle")}
            </div>
          </div>
          <LangPicker compact />
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-[0.18em] text-white/50 mb-1.5">
              {t("common.email")}
            </label>
            <input
              type="email"
              autoFocus
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
              placeholder={t("login.placeholder.email")}
              data-testid="aff-login-email"
            />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-[0.18em] text-white/50 mb-1.5">
              {t("common.password")}
            </label>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
              placeholder="••••••••••"
              data-testid="aff-login-password"
            />
          </div>
        </div>

        {error && (
          <div
            className="text-sm text-red-400 border-l-2 border-red-500 pl-3 py-1 font-mono"
            data-testid="aff-login-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-white text-black py-2.5 text-sm font-medium tracking-wide hover:bg-white/90 active:translate-y-px transition-all disabled:opacity-50"
          data-testid="aff-login-submit"
        >
          {busy ? t("login.busy") : t("login.cta")}
        </button>

        <div className="text-center text-xs font-mono text-white/40">
          <Link
            to="/affiliate/forgot"
            className="text-white/40 hover:text-white/70 mr-3"
            data-testid="aff-login-to-forgot"
          >
            {t("login.forgot")}
          </Link>
          ·{" "}
          {t("login.new_to")}{" "}
          <Link
            to="/affiliate/signup"
            className="text-emerald-400 hover:text-emerald-300"
            data-testid="aff-login-to-signup"
          >
            {t("login.apply")}
          </Link>
        </div>
      </form>
    </div>
  );
}
